import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Navigate } from "react-router-dom";
import { Lock } from "lucide-react";

interface StaffMember {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  team: string;
}

interface StaffCredentials {
  id: string;
  staff_member_id: string;
  asana_api_key?: string;
  asana_user_gid?: string;
}

export const ProfileSettings = () => {
  const { user, session, loading: authLoading, isAuthenticated, staffMember: authStaffMember } = useAuth();
  const [staffMember, setStaffMember] = useState<StaffMember | null>(null);
  const [credentials, setCredentials] = useState<StaffCredentials | null>(null);
  const [asanaApiKey, setAsanaApiKey] = useState("");
  const [asanaUserGid, setAsanaUserGid] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      const provider = user.app_metadata?.provider;
      setIsGoogleUser(provider === 'google');
    }
  }, [user]);

  useEffect(() => {
    const fetchStaffData = async () => {
      if (!user || !session) {
        console.log('No user or session, skipping fetch');
        setDataLoading(false);
        return;
      }
      
      try {
        setDataLoading(true);
        
        // Use staff member from auth hook if available, otherwise fetch
        if (authStaffMember) {
          setStaffMember(authStaffMember);
          
          // Fetch credentials via edge function to bypass RLS issues
          try {
            const { data, error } = await supabase.functions.invoke('manage-staff-credentials', {
              body: {
                action: 'fetch'
              },
              headers: {
                Authorization: `Bearer ${session.access_token}`
              }
            });

            if (error) {
              console.error('Edge function error:', error);
            } else if (data?.success && data?.data) {
              setCredentials(data.data);
              setAsanaApiKey(data.data.asana_api_key || "");
              setAsanaUserGid(data.data.asana_user_gid || "");
            }
          } catch (fetchError) {
            console.error('Failed to fetch credentials via edge function:', fetchError);
          }
        } else {
          // Fallback: fetch via profile lookup
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('staff_member_id')
            .eq('user_id', user.id)
            .single();

          if (profileError || !profile?.staff_member_id) {
            console.error('Error fetching profile or no staff member ID found');
            return;
          }

          // Now fetch staff member data using the staff_member_id
          const { data: staffData, error: staffError } = await supabase
            .from('staff_members')
            .select('*')
            .eq('id', profile.staff_member_id)
            .single();
          
          if (staffError || !staffData) {
            console.error('Error fetching staff member or no data found');
            return;
          }

          setStaffMember(staffData);
          
          // Fetch credentials via edge function to bypass RLS issues  
          try {
            const { data, error } = await supabase.functions.invoke('manage-staff-credentials', {
              body: {
                action: 'fetch'
              },
              headers: {
                Authorization: `Bearer ${session.access_token}`
              }
            });

            if (error) {
              console.error('Edge function error:', error);
            } else if (data?.success && data?.data) {
              setCredentials(data.data);
              setAsanaApiKey(data.data.asana_api_key || "");
              setAsanaUserGid(data.data.asana_user_gid || "");
            }
          } catch (fetchError) {
            console.error('Failed to fetch credentials via edge function:', fetchError);
          }
        }
      } catch (error) {
        console.error('Error in fetchStaffData:', error);
      } finally {
        setDataLoading(false);
      }
    };

    fetchStaffData();
  }, [user, session, authStaffMember]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted p-6 flex items-center justify-center">
        <div>Loading authentication...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/staff-login" replace />;
  }

  const handleSaveAsanaKey = async () => {
    if (!session || !user) {
      toast({
        title: "Error",
        description: "Authentication required. Please log in again.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      console.log('Saving Asana credentials via edge function');

      const { data, error } = await supabase.functions.invoke('manage-staff-credentials', {
        body: {
          action: 'upsert',
          asana_api_key: asanaApiKey || null,
          asana_user_gid: asanaUserGid || null
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Unknown error occurred');
      }

      console.log('Credentials saved successfully:', data.data);
      setCredentials(data.data);

      toast({
        title: "Success",
        description: "Asana integration settings saved successfully",
      });
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: `Failed to save Asana integration settings: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveAsanaKey = async () => {
    if (!session || !user) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('manage-staff-credentials', {
        body: {
          action: 'delete'
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Unknown error occurred');
      }

      setCredentials(null);
      setAsanaApiKey("");
      setAsanaUserGid("");
      toast({
        title: "Success",
        description: "Asana integration settings removed successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove Asana integration settings",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({ title: "Error", description: "New passwords do not match", variant: "destructive" });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: "Error", description: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    setIsChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast({ title: "Success", description: "Password updated successfully" });
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to update password", variant: "destructive" });
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (dataLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted p-6 flex items-center justify-center">
        <div>Loading staff information...</div>
      </div>
    );
  }

  if (!staffMember) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted p-6 flex items-center justify-center">
        <div>Staff information not found. Please contact an administrator.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Profile Settings</h1>
          <p className="text-muted-foreground mt-2">
            Manage your profile information and integrations
          </p>
        </div>

        <div className="grid gap-6">
          {/* Profile Information */}
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input value={staffMember.first_name || ""} disabled />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input value={staffMember.last_name || ""} disabled />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Email</Label>
                  <Input value={staffMember.email} disabled />
                </div>
                <div>
                  <Label>Team</Label>
                  <Input value={staffMember.team} disabled />
                </div>
              </div>
              <div>
                <Label>Role</Label>
                <Input value={staffMember.role} disabled />
              </div>
            </CardContent>
          </Card>

          {/* Asana Integration */}
          <Card>
            <CardHeader>
              <CardTitle>Asana Integration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="asana-key">Asana Personal Access Token</Label>
                <Input
                  id="asana-key"
                  type="password"
                  value={asanaApiKey}
                  onChange={(e) => setAsanaApiKey(e.target.value)}
                  placeholder="Enter your Asana Personal Access Token"
                />
                <p className="text-sm text-muted-foreground mt-2">
                  You can generate a Personal Access Token in your Asana account settings
                </p>
              </div>
              <div>
                <Label htmlFor="asana-gid">Asana User GID</Label>
                <Input
                  id="asana-gid"
                  type="text"
                  value={asanaUserGid}
                  onChange={(e) => setAsanaUserGid(e.target.value)}
                  placeholder="Enter your Asana User GID"
                />
                <p className="text-sm text-muted-foreground mt-2">
                  Your unique Asana User ID (e.g., 1170011823576621). You can find this in your Asana profile or ask an admin.
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={handleSaveAsanaKey}
                  disabled={isLoading || !asanaApiKey.trim() || !asanaUserGid.trim()}
                >
                  {isLoading ? "Saving..." : "Save Integration Settings"}
                </Button>
                {credentials && (
                  <Button 
                    variant="destructive"
                    onClick={handleRemoveAsanaKey}
                    disabled={isLoading}
                  >
                    Remove Integration
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Change Password - only for non-Google users */}
          {!isGoogleUser && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="h-5 w-5" />
                  Change Password
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                  />
                </div>
                <Button
                  onClick={handleChangePassword}
                  disabled={isChangingPassword || !newPassword.trim() || !confirmPassword.trim()}
                >
                  {isChangingPassword ? "Updating..." : "Update Password"}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;